import React, { useState } from 'react';
import { Box, Tooltip, IconButton, Typography, Snackbar, Alert } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

interface GuidDisplayProps {
  guid: string;
  label?: string;
  abbreviate?: boolean;
}

const GuidDisplay: React.FC<GuidDisplayProps> = ({ guid, label, abbreviate = true }) => {
  const [showFull, setShowFull] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  if (!guid) return <Typography variant="caption" color="text.secondary">—</Typography>;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(guid);
    setSnackbarOpen(true);
  };

  const toggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowFull(!showFull);
  };

  const displayValue = showFull ? guid : (abbreviate ? `${guid.substring(0, 8)}...` : guid);

  return (
    <Box display="flex" alignItems="center" gap={0.5} component="span">
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {label}:
        </Typography>
      )}
      <Tooltip title={guid}>
        <Typography 
            variant="body2" 
            fontFamily="monospace" 
            component="span"
            sx={{ 
                cursor: 'pointer',
                bgcolor: showFull ? 'action.hover' : 'transparent',
                px: showFull ? 0.5 : 0,
                borderRadius: 1
            }}
            onClick={toggleVisibility}
        >
          {displayValue}
        </Typography>
      </Tooltip>
      
      <Tooltip title={showFull ? "Hide" : "Show full GUID"}>
        <IconButton size="small" onClick={toggleVisibility} sx={{ p: 0.5 }}>
            {showFull ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
        </IconButton>
      </Tooltip>

      <Tooltip title="Copy GUID">
        <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          GUID copied to clipboard
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GuidDisplay;
